import type { LabStatusCategory } from "@/lib/lab-status";

import type { PageParams } from "@/types/pagination";

/** Junction returns compound statuses like "received.walk_in_test.requisition_created".
 *  We store the raw string — don't exhaustively match. */
export type LabOrderStatus = string;

// How the specimen gets drawn on an ACCESS order. Deliberately separate from
// LabCollectionMethod, which is Junction's vocabulary — both Access mobile
// options would collapse into its single "at_home_phlebotomy" and lose the
// distinction that decides which code rides along on the CSV.
export type AccessCollectionMethod =
  | "practice_draw"
  | "walk_in_lab"
  | "mobile_draw"
  | "local_mobile_draw";

export type FastingStatus = "yes" | "no" | "not_indicated";

/** One Access account a clinic orders under. Locations are assigned to an
 *  account; a location without one orders under the clinic's default. */
export interface AccessLabAccount {
  id: string;
  clientNumber: string;
  name: string | null;
  isDefault: boolean;
  /** The live locations assigned to this account. */
  locationIds: string[];
  packagingNotes: string | null;
}

export interface AccessLabAccountCreate {
  clientNumber: string;
  name: string | null;
  packagingNotes: string | null;
}

/** The client number is fixed once saved. */
export interface AccessLabAccountUpdate {
  name: string | null;
  packagingNotes?: string | null;
}

/** What the order form needs to pick an account. */
export interface AccessLabOrderingAccounts {
  accounts: AccessLabAccount[];
  /** Live locations assigned to an account that is no longer live. Orders
   *  there must not quietly fall back to the default. */
  orphanedLocationIds: string[];
}

/** A live location and the account it orders under, null meaning the default. */
export interface AccessLabLocationAccount {
  locationId: string;
  accessLabAccountId: string | null;
}

export type LabCollectionMethod =
  | "walk_in_test"
  | "at_home_phlebotomy"
  | "testkit"
  | "on_site_collection";

export type LabBillingType =
  | "client_bill"
  | "commercial_insurance"
  | "patient_bill"
  | "patient_bill_passthrough";

/** Which integration an order came from. Junction orders are authored in
 *  Decoda's wizard; Fullscript orders are authored in Fullscript's embed;
 *  Access orders are authored in Decoda's own (much simpler) Access flow and
 *  placed by uploading a CSV — see `access-lab-order-flow.tsx`; uploaded orders
 *  are outside results sent to Decoda (through the API) with no vendor on the
 *  other end; in-house orders go to the clinic's own analyzer through its lab
 *  interface — see `in-house-lab-order-flow.tsx`. */
export type LabVendor =
  | "junction"
  | "fullscript"
  | "access"
  | "uploaded"
  | "in_house";

export const LAB_VENDORS: readonly LabVendor[] = [
  "junction",
  "fullscript",
  "access",
  "uploaded",
  "in_house",
];

/**
 * Whether a value off the wire is a vendor this build knows how to read.
 *
 * Every status helper branches on the vendor, so casting an unrecognised one
 * silently gives it Junction's lifecycle semantics. Callers use this to tell
 * "no vendor was sent" — which really does mean Junction — from "a vendor this
 * build has never heard of", which is a reason to reload rather than guess.
 */
export function isLabVendor(value: unknown): value is LabVendor {
  return LAB_VENDORS.includes(value as LabVendor);
}

export type LabInternalStatus = "draft" | "submitted" | "failed";

export type LabOrderOrigin = "internal" | "external_sync";

export type LabOrderItemType = "lab_test" | "marker";

export type BiomarkerResultType =
  | "numeric"
  | "range"
  | "comment"
  | "coded_value";

export type ClinicalInterpretation = "normal" | "abnormal" | "critical";

export interface AoEAnswerOption {
  id: number;
  code: string;
  value: string;
}

export interface AoEQuestion {
  id: number;
  required: boolean;
  code: string;
  value: string;
  type: "choice" | "text" | "numeric" | "multi_choice";
  sequence: number;
  answers?: AoEAnswerOption[];
  constraint?: string;
  default?: string;
}

export interface AoEAnswer {
  markerId: number;
  questionId: number;
  answer: string;
}

export interface Marker {
  id: number;
  name: string;
  slug: string;
  description?: string;
  lab_id: number;
  provider_id?: string;
  type?: string;
  unit?: string;
  price?: string;
  a_la_carte_enabled?: boolean;
  common_tat_days?: number | null;
  worst_case_tat_days?: number | null;
  is_orderable?: boolean;
  aoe?: {
    questions: AoEQuestion[];
  } | null;
}

export interface LabTestLab {
  id: number;
  slug: string;
  name: string;
  first_line_address?: string;
  city?: string;
  zipcode?: string;
  collection_methods?: string[];
  sample_types?: string[];
}

export type LabTestStatus = "active" | "pending_approval" | "inactive";

export interface LabTest {
  id: string;
  name: string;
  slug?: string;
  sample_type?: string;
  method?: string;
  price?: number;
  is_active?: boolean;
  /** Prefer over `is_active`, which Junction deprecated in its favour. */
  status?: LabTestStatus;
  fasting?: boolean;
  lab?: LabTestLab;
  markers?: Marker[];
  /** False only on panels a team authored; Junction's own stock panels are true. */
  auto_generated?: boolean;
  common_tat_days?: number | null;
  worst_case_tat_days?: number | null;
}

/** A row from the Access (Comtron/LabGen) test compendium — the catalog
 *  searched when picking a test code for an Access lab order. Search-only:
 *  there is deliberately no browse-all endpoint over this table. */
export interface AccessLabTestCode {
  code: string;
  name: string;
  isActive: boolean;
  collectionInstructions?: string | null;
}

/** Mirrors AccessCollectionInstructions in decoda/schemas/lab.py. */
export interface AccessCollectionInstructions {
  tests: { code: string; name: string; instructions: string }[];
  packagingNotes: string | null;
}

/** Mirrors AccessLabPanel in decoda/schemas/lab.py: a clinic's named group of Access
 * codes, stored in Decoda because Access has no panel API. */
export interface AccessLabPanel {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  tests: AccessLabTestCode[];
  /** What the lab charges for the whole panel, in cents. */
  price: number | null;
}

/** Mirrors AccessLabPanelCreate in decoda/schemas/lab.py. */
export interface AccessLabPanelCreate {
  name: string;
  description?: string;
  testCodes: string[];
  price?: number;
}

/** Mirrors AccessLabPanelUpdate in decoda/schemas/lab.py. `price: null` clears it. */
export interface AccessLabPanelUpdate {
  name?: string;
  active?: boolean;
  price?: number | null;
}

/** Mirrors LabPanelCreate in decoda/schemas/lab.py. camelCase because this is a
 * Decoda-owned request body; the snake_case above is Junction's response, proxied. */
export interface LabPanelCreate {
  name: string;
  description: string;
  method: LabCollectionMethod;
  markerIds: number[];
  fasting?: boolean;
}

/** Mirrors LabPanelUpdate in decoda/schemas/lab.py. */
export interface LabPanelUpdate {
  name?: string;
  active?: boolean;
}

export interface LabInfo {
  id: number;
  slug: string;
  name: string;
  first_line_address?: string;
  city?: string;
  zipcode?: string;
  collection_methods?: string[];
  sample_types?: string[];
}

// Raw Junction team lab account (GET /lab-accounts passes it through, so this
// stays snake_case like LabInfo). `lab` is a Junction lab enum ("labcorp",
// "quest", …) matched against CartBucket.labSlug; `id` is the UUID sent as the
// order's lab_account_id.
export interface LabAccount {
  id: string;
  lab: string;
  status: string;
  account_name?: string | null;
  provider_account_id?: string;
  allowed_billing?: Record<string, string[]>;
  delegated_flow?: string;
}

export interface PSCLocation {
  metadata: {
    name: string;
    state: string;
    city: string;
    zip_code: string;
    first_line: string;
    second_line?: string;
    phone_number?: string;
    hours?: Record<string, string>;
  };
  distance: number;
  site_code: string;
  supported_bill_types?: string[];
  location?: {
    lng: number;
    lat: number;
  };
}

export interface JunctionPayor {
  payorCode: string;
  name: string;
  aliases: string[];
  orgAddress: {
    firstLine: string | null;
    secondLine: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    accessNotes: string | null;
  } | null;
  source: string | null;
}

export interface LastJunctionPayor {
  payorCode: string;
  payorName: string;
}

export interface CartBucket {
  labId: number;
  labName: string;
  labSlug: string;
  presets: LabTest[];
  markers: Marker[];
  collectionMethod: LabCollectionMethod | null;
  // Where `collectionMethod` came from. "staff" is an explicit pick and sticks
  // through any cart edit; "preset" is a panel's Junction default, which is only
  // a suggestion and is recomputed (or dropped) whenever the panel set changes.
  // Without this the two are indistinguishable, and a default inherited from a
  // panel that has since been removed or overruled would still satisfy the
  // "every order has a method" placement gate.
  methodSource: "staff" | "preset" | null;
  // Junction lab account UUID for this lab. Auto-set when the lab has exactly
  // one active account; picked by staff when it has several; null otherwise.
  labAccountId: string | null;
  psc: PSCLocation | null;
}

export type OrderSubmissionStatus =
  | "pending"
  | "submitting"
  | "success"
  | "error";

export interface OrderSubmissionEntry {
  bucketIndex: number;
  labName: string;
  labSlug: string;
  status: OrderSubmissionStatus;
  error?: string;
  isRetryable?: boolean;
}

export interface LabOrderEvent {
  id: string;
  vendorEventId?: number;
  statusCode?: string;
  eventTimestamp?: string;
}

export interface LabOrderItem {
  id: string;
  itemType: LabOrderItemType;
  vendorCatalogId: string;
  displayName?: string;
  isStripped?: boolean;
  sourcePresetId?: string;
}

export interface LabBiomarkerResult {
  id: string;
  markerSlug?: string;
  markerName: string;
  loincCode?: string;
  resultType?: BiomarkerResultType;
  valueNumeric?: number;
  valueDisplay?: string;
  unit?: string;
  minRangeValue?: number;
  maxRangeValue?: number;
  referenceRangeText?: string;
  clinicalInterpretation?: ClinicalInterpretation;
  notes?: string;
  /** The lab's own flag (H, L, HH, LL); in-house results only, for now. */
  abnormalFlag?: string;
  /** partial, final or corrected; in-house results only, for now. */
  resultStatus?: string;
  resultTimestamp?: string;
  sourceMarkers?: unknown[];
}

export type BiomarkerRangeSource = "lab" | "clinic";

/** One result of a marker, dated to when the specimen was drawn. */
export interface LabBiomarkerHistoryResult {
  id: string;
  labOrderId: string;
  labName?: string;
  valueNumeric?: number;
  valueDisplay?: string;
  minRangeValue?: number;
  maxRangeValue?: number;
  referenceRangeText?: string;
  rangeSource?: BiomarkerRangeSource;
  clinicalInterpretation?: ClinicalInterpretation;
  collectedAt: string;
}

/** Every result a patient has for one marker in one unit, newest first. */
export interface LabBiomarkerHistory {
  key: string;
  name: string;
  unit?: string;
  loincCode?: string;
  fieldId?: string;
  results: LabBiomarkerHistoryResult[];
}

export interface LabOrder {
  id: string;
  patientId: string;
  labOrderSessionId?: string;
  vendor: LabVendor;
  vendorOrderId?: string;
  vendorTransactionId?: string;
  /** Fullscript only: the order number printed on the patient's receipt. Absent
   *  until the lab is bought. */
  fullscriptOrderNumber?: string;
  internalStatus: LabInternalStatus;
  vendorStatus?: LabOrderStatus;
  origin: LabOrderOrigin;
  collectionMethod?: LabCollectionMethod;
  labSlug?: string;
  labName?: string;
  errorMessage?: string;
  /** A report PDF is stored with the order. Read for in-house orders, whose lab
   *  may send results without one. */
  hasResultPdf?: boolean;
  // From local_metadata
  billingType?: LabBillingType;
  icdCodes?: string[];
  clinicalNotes?: string;
  physicianFirstName?: string;
  physicianLastName?: string;
  physicianNpi?: string;
  physicianMode?: string;
  /** Uploaded orders only: the outside ordering provider, as sent with the results. Free text,
   *  with no internal provider to resolve it to — distinct from physicianFirstName/LastName,
   *  which carry the clinic's own ordering physician on a Junction order. */
  orderingProviderName?: string;
  priority?: string;
  pscName?: string;
  pscAddress?: string;
  labAccountId?: string;
  /** Access only: the account the order went out under, recorded at submit. */
  accessLabAccountId?: string;
  accessClientNumber?: string;
  /** Access only: how the specimen gets drawn, and — for a practice draw — the
   *  actual collection the provider entered. For any other modality the date
   *  that reached Access is its own order time, not a real draw, so the method
   *  has to be read alongside the date. */
  accessCollectionMethod?: AccessCollectionMethod;
  fastingStatus?: FastingStatus;
  collectionDate?: string;
  collectionTime?: string;
  insuranceCoverageId?: string;
  junctionPayorCode?: string;
  junctionPayorName?: string;
  medicalProxy?: LabMedicalProxy | null;
  // From vendor_metadata
  labTestName?: string;
  sampleType?: string;
  fasting?: string;
  sampleId?: string;
  // Timestamps
  createdDate?: string;
  updatedDate?: string;
  resultsViewedAt?: string;
  // Session info
  locationId?: string;
  physicianId?: string;
  creatorId?: string;
  // Nested
  events: LabOrderEvent[];
  items: LabOrderItem[];
  aoeAnswers?: AoEAnswer[];
  results: LabBiomarkerResult[];
}

export interface LabOrderListItem {
  id: string;
  vendor: LabVendor;
  vendorOrderId?: string;
  internalStatus: LabInternalStatus;
  vendorStatus?: LabOrderStatus;
  collectionMethod?: LabCollectionMethod;
  labSlug?: string;
  labName?: string;
  labOrderSessionId?: string;
  fullscriptTreatmentPlanId?: string;
  createdDate?: string;
  vendorUpdatedAt?: string;
  resultsViewedAt?: string;
  items: LabOrderItem[];
}

/** The parent or guardian Junction requires on every order for a patient
 *  under 18. Mirrors `LabMedicalProxy` in decoda/schemas/lab.py. */
export interface LabMedicalProxy {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email?: string | null;
  address: string;
  addressLineTwo?: string | null;
  city: string;
  state: string;
  zipCode: string;
}

export interface LabOrderCreate {
  // Which vendor this order belongs to. Omitted means Junction — every
  // existing (Junction) caller sends this field only for Access, so the
  // absent-means-Junction default matches the backend's own
  // `vendor: LabVendor = LabVendor.JUNCTION`.
  vendor?: LabVendor;
  labTestIds?: string[];
  markerIds?: number[];
  collectionMethod?: LabCollectionMethod;
  billingType?: LabBillingType;
  icdCodes?: string[];
  clinicalNotes?: string;
  physicianFirstName?: string;
  physicianLastName?: string;
  physicianNpi?: string;
  physicianMode?: string;
  priority?: boolean;
  pscName?: string;
  pscAddress?: string;
  labSlug?: string;
  labName?: string;
  labAccountId?: string;
  insuranceCoverageId?: string;
  junctionPayorCode?: string | null;
  junctionPayorName?: string | null;
  aoeAnswers?: AoEAnswer[];
  patientDetails?: Record<string, unknown>;
  patientAddress?: Record<string, unknown>;
  medicalProxy?: LabMedicalProxy | null;
  physicianId?: string;
  items?: LabOrderCreateItem[];
  isDraft?: boolean;
  // Access only, for now: the actual specimen collection date/time for a
  // practice draw, as entered by the provider at authoring time — not
  // derived from when the order happens to be submitted. Plain ISO strings
  // ("YYYY-MM-DD" / 24h "HH:MM"); omitted means the backend falls back to
  // submission time. See `decoda/schemas/lab.py`'s `LabOrderCreate` for why.
  collectionDate?: string;
  collectionTime?: string;
  accessCollectionMethod?: AccessCollectionMethod;
  fastingStatus?: FastingStatus;
}

export interface LabOrderCreateItem {
  itemType: LabOrderItemType;
  vendorCatalogId: string;
  displayName?: string;
  isStripped?: boolean;
  sourcePresetId?: string;
}

export interface LabOrderBatchCreate {
  patientId: string;
  locationId?: string;
  physicianId?: string;
  orders: LabOrderCreate[];
}

/** Filters for the clinic-wide `/labs` list — mirrors `LabOrderFetchParams` in `decoda/schemas/lab.py`. */
export interface LabOrderFetchParams extends PageParams {
  orderIds?: string[];
  patientId?: string;
  patientName?: string;
  status?: LabStatusCategory[];
  vendor?: LabVendor[];
  physicianIds?: string[];
  creatorIds?: string[];
  locationIds?: string[];
  includeUnassigned?: boolean;
  collectionMethod?: LabCollectionMethod[];
  labSlug?: string[];
  createdDate?: (string | null)[];
  testName?: string;
  tz?: string;
}

/** One row of the clinic-wide list — mirrors `LabOrderTableItem` in `decoda/schemas/lab.py`. */
export interface LabOrderTableItem extends LabOrderListItem {
  patientId: string;
  patientFirstName?: string;
  patientLastName?: string;
  statusCategory: LabStatusCategory;
  physicianId?: string;
  physicianName?: string;
  creatorId?: string;
  creatorName?: string;
  locationId?: string;
  locationName?: string;
}

/** One test on the in-house lab's menu; `code` is what goes to the lab interface. */
export interface InHouseLabTest {
  code: string;
  name: string;
  specimenCode: string;
  specimenName: string;
}

export interface InHouseLabMenu {
  tests: InHouseLabTest[];
}
